import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { useAuth } from './context/AuthContext';
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
  ButtonGroup,
  Row,
  Col,
  Card
} from 'react-bootstrap';
import { isSuperAdmin } from './utils/superAdmin';

/**
 * 交易流水帳管理組件
 * 提供收入/支出交易記錄的查詢、新增（手動入帳）功能
 */
function TransactionManagement() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  // RWD 狀態
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // 監聽視窗大小變化
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 狀態管理
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal 狀態
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentDetail, setCurrentDetail] = useState(null);

  // 篩選條件
  const [filters, setFilters] = useState({
    ssid: user?.e_ssid || '',
    term_id: '',
    s_id: '',
    direction: 'all',
    source: 'all',
    start_date: '',
    end_date: ''
  });

  // 新增交易表單
  const [newTransaction, setNewTransaction] = useState({
    ssid: user?.e_ssid || '',
    term_id: '',
    s_id: '',
    direction: 'income',
    amount: 0,
    txn_date: new Date().toISOString().split('T')[0],
    payment_method: 'transfer',
    payment_ref: '',
    memo: ''
  });

  // 科目列表
  const [subjects, setSubjects] = useState([]);

  // 社別列表
  const [societies, setSocieties] = useState([]);

  // 屆別列表
  const [terms, setTerms] = useState([]);

  // 統計資料
  const [stats, setStats] = useState({
    total_income: 0,
    total_expense: 0,
    net_amount: 0,
    count: 0
  });

  // 載入社別列表
  const loadSocieties = async () => {
    try {
      const response = await axios.get('/stores', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setSocieties(response.data);
      }
    } catch (err) {
      console.error('載入社別列表失敗:', err);
    }
  };

  // 載入屆別列表
  const loadTerms = async (ssid) => {
    try {
      if (!ssid) {
        setTerms([]);
        return;
      }

      const response = await axios.get('/finance/terms', {
        params: { ssid },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const termsData = response.data.data;
        setTerms(termsData);

        // 自動選擇啟用的屆別
        if (termsData.length > 0 && filters.ssid && !filters.term_id) {
          const activeTerm = termsData.find(term => term.is_active === 1 || term.is_active === '1');
          if (activeTerm) {
            setFilters(prev => ({ ...prev, term_id: activeTerm.term_id }));
          }
        }
      }
    } catch (err) {
      console.error('載入屆別列表失敗:', err);
    }
  };

  // 載入科目列表
  const loadSubjects = async () => {
    try {
      const response = await axios.get('/finance/subjects', {
        params: { active_only: 'true' },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSubjects(response.data.data);
      }
    } catch (err) {
      console.error('載入科目列表失敗:', err);
    }
  };

  // 載入交易記錄列表
  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filters.ssid) params.ssid = filters.ssid;
      if (filters.term_id) params.term_id = filters.term_id;
      if (filters.s_id) params.s_id = filters.s_id;
      if (filters.direction !== 'all') params.direction = filters.direction;
      if (filters.source !== 'all') params.source = filters.source;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await axios.get('/finance/transactions', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTransactions(response.data.data);

        // 計算統計
        const income = response.data.data
          .filter(t => t.direction === 'income')
          .reduce((sum, t) => sum + parseFloat(t.amount), 0);

        const expense = response.data.data
          .filter(t => t.direction === 'expense')
          .reduce((sum, t) => sum + parseFloat(t.amount), 0);

        setStats({
          total_income: income,
          total_expense: expense,
          net_amount: income - expense,
          count: response.data.data.length
        });
      }
    } catch (err) {
      setError('載入交易記錄失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 初始化載入
  useEffect(() => {
    loadSocieties();
    loadSubjects();

    if (filters.ssid) {
      loadTerms(filters.ssid);
    }
  }, []);

  // 當篩選條件變更時重新載入
  useEffect(() => {
    if (filters.ssid && filters.term_id) {
      loadTransactions();
    }
  }, [filters]);

  // 當社別改變時載入對應的屆別
  useEffect(() => {
    if (filters.ssid) {
      setFilters(prev => ({ ...prev, term_id: '' }));
      loadTerms(filters.ssid);
    } else {
      setTerms([]);
      setFilters(prev => ({ ...prev, term_id: '' }));
    }
  }, [filters.ssid]);

  // 開啟新增交易彈窗
  const openNewTransactionModal = () => {
    setNewTransaction({
      ssid: filters.ssid || user?.e_ssid || '',
      term_id: filters.term_id || '',
      s_id: '',
      direction: 'income',
      amount: 0,
      txn_date: new Date().toISOString().split('T')[0],
      payment_method: 'transfer',
      payment_ref: '',
      memo: ''
    });

    setShowModal(true);
  };

  // 提交新增交易
  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);

      // 驗證
      if (!newTransaction.ssid || !newTransaction.term_id || !newTransaction.s_id) {
        setError('社別、屆別和科目為必填欄位');
        return;
      }

      if (!newTransaction.amount || parseFloat(newTransaction.amount) <= 0) {
        setError('請輸入正確的金額');
        return;
      }

      const response = await axios.post('/finance/transactions', {
        ...newTransaction,
        created_by: user?.e_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('交易記錄新增成功！');
        setShowModal(false);
        loadTransactions();
      }
    } catch (err) {
      setError(err.response?.data?.error || '新增交易記錄失敗');
    }
  };

  // 查看交易詳情
  const viewDetail = async (txn_id) => {
    try {
      const response = await axios.get(`/finance/transactions/${txn_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setCurrentDetail(response.data.data);
        setShowDetailModal(true);
      }
    } catch (err) {
      setError('載入交易詳情失敗：' + (err.response?.data?.error || err.message));
    }
  };

  // 刪除交易（僅限手動建立的）
  const deleteTransaction = async (txn_id, source) => {
    try {
      if (source !== 'manual') {
        setError('只能刪除手動建立的交易記錄');
        return;
      }

      if (!window.confirm('確定要刪除此交易記錄嗎？此操作無法復原！')) {
        return;
      }

      setError(null);
      setSuccess(null);

      const response = await axios.delete(`/finance/transactions/${txn_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('交易記錄刪除成功');
        setShowDetailModal(false);
        loadTransactions();
      }
    } catch (err) {
      setError(err.response?.data?.error || '刪除失敗');
    }
  };

  // 取得方向顏色
  const getDirectionColor = (direction) => {
    return direction === 'income' ? 'success' : 'danger';
  };

  // 取得方向文字
  const getDirectionText = (direction) => {
    return direction === 'income' ? '收入' : '支出';
  };

  // 取得來源文字
  const getSourceText = (source) => {
    const texts = {
      reimbursement: '請款單',
      manual: '手動入帳',
      adjustment: '調整'
    };
    return texts[source] || source;
  };

  // 取得付款方式文字
  const getPaymentMethodText = (method) => {
    const texts = {
      cash: '現金',
      transfer: '轉帳',
      check: '支票',
      other: '其他'
    };
    return texts[method] || '-';
  };

  // 格式化金額
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const isAdmin = isSuperAdmin(user?.e_id) || user?.e_position === 17;

  return (
    <Container fluid className={isMobile ? "py-2 px-2" : "py-4"}>
      <h2 className={isMobile ? "mb-3 fs-5" : "mb-4"}>
        📊 {isMobile ? "交易流水帳" : "財務管理 - 交易流水帳"}
      </h2>

      {/* 錯誤/成功訊息 */}
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      {/* 統計卡片 */}
      {filters.ssid && filters.term_id && (
        <Row className={isMobile ? "mb-3 g-2" : "mb-4"}>
          <Col xs={6} md={3}>
            <Card className="text-center h-100">
              <Card.Body className={isMobile ? "p-2" : ""}>
                <small className="text-muted">{isMobile ? "收入" : "總收入"}</small>
                <div className={`text-success ${isMobile ? "fs-6" : "h4"}`}>
                  {formatAmount(stats.total_income)}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="text-center h-100">
              <Card.Body className={isMobile ? "p-2" : ""}>
                <small className="text-muted">{isMobile ? "支出" : "總支出"}</small>
                <div className={`text-danger ${isMobile ? "fs-6" : "h4"}`}>
                  {formatAmount(stats.total_expense)}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="text-center h-100">
              <Card.Body className={isMobile ? "p-2" : ""}>
                <small className="text-muted">淨額</small>
                <div className={`${stats.net_amount >= 0 ? 'text-success' : 'text-danger'} ${isMobile ? "fs-6" : "h4"}`}>
                  {formatAmount(stats.net_amount)}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="text-center h-100">
              <Card.Body className={isMobile ? "p-2" : ""}>
                <small className="text-muted">筆數</small>
                <div className={`text-primary ${isMobile ? "fs-6" : "h4"}`}>{stats.count}</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* 篩選工具列 */}
      <Card className="mb-3">
        <Card.Body className={isMobile ? "p-2" : ""}>
          <h6 className={isMobile ? "mb-2" : "mb-3"}>{isMobile ? "篩選" : "篩選條件"}</h6>
          <Row className={isMobile ? "g-2" : ""}>
            <Col xs={6} md={3}>
              <Form.Group className="mb-2">
                {!isMobile && <Form.Label>社別</Form.Label>}
                <Form.Select
                  size={isMobile ? "sm" : undefined}
                  value={filters.ssid}
                  onChange={(e) => setFilters({ ...filters, ssid: e.target.value })}
                >
                  <option value="">選擇社別</option>
                  {societies.map(society => (
                    <option key={society.ss_id} value={society.ss_id}>
                      {society.ss_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group className="mb-2">
                {!isMobile && <Form.Label>屆別</Form.Label>}
                <Form.Select
                  size={isMobile ? "sm" : undefined}
                  value={filters.term_id}
                  onChange={(e) => setFilters({ ...filters, term_id: e.target.value })}
                  disabled={!filters.ssid}
                >
                  <option value="">選擇屆別</option>
                  {terms.map(term => (
                    <option key={term.term_id} value={term.term_id}>
                      {isMobile ? `第${term.term_no}屆` : `${term.term_name}（第 ${term.term_no} 屆）`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group className="mb-2">
                {!isMobile && <Form.Label>收支類型</Form.Label>}
                <Form.Select
                  size={isMobile ? "sm" : undefined}
                  value={filters.direction}
                  onChange={(e) => setFilters({ ...filters, direction: e.target.value })}
                >
                  <option value="all">全部類型</option>
                  <option value="income">收入</option>
                  <option value="expense">支出</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group className="mb-2">
                {!isMobile && <Form.Label>來源</Form.Label>}
                <Form.Select
                  size={isMobile ? "sm" : undefined}
                  value={filters.source}
                  onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                >
                  <option value="all">全部來源</option>
                  <option value="reimbursement">請款單</option>
                  <option value="manual">手動入帳</option>
                  <option value="adjustment">調整</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          {/* 進階篩選 - 桌面版顯示更多選項 */}
          {!isMobile && (
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>科目</Form.Label>
                  <Form.Select
                    value={filters.s_id}
                    onChange={(e) => setFilters({ ...filters, s_id: e.target.value })}
                  >
                    <option value="">全部科目</option>
                    {subjects.map(s => (
                      <option key={s.s_id} value={s.s_id}>
                        {s.s_code} - {s.s_title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>起始日期</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>結束日期</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Form.Group className="mb-3 w-100">
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={loadTransactions}
                    disabled={!filters.ssid || !filters.term_id}
                  >
                    🔍 查詢
                  </Button>
                </Form.Group>
              </Col>
            </Row>
          )}
          {/* 手機版查詢按鈕 */}
          {isMobile && (
            <div className="d-flex gap-2 mt-2">
              <Button
                variant="primary"
                size="sm"
                className="flex-grow-1"
                onClick={loadTransactions}
                disabled={!filters.ssid || !filters.term_id}
              >
                🔍 查詢
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={openNewTransactionModal}
                disabled={!filters.ssid || !filters.term_id}
              >
                ➕ 入帳
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* 操作按鈕 - 桌面版 */}
      {!isMobile && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            {filters.ssid && filters.term_id && (
              <span className="text-muted">
                共 {transactions.length} 筆交易記錄
              </span>
            )}
          </div>
          <Button
            variant="primary"
            onClick={openNewTransactionModal}
            disabled={!filters.ssid || !filters.term_id}
          >
            ➕ 手動入帳
          </Button>
        </div>
      )}

      {/* 交易記錄列表 */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : !filters.ssid || !filters.term_id ? (
        <Alert variant="info" className={isMobile ? "py-2 small" : ""}>
          請先選擇社別和屆別
        </Alert>
      ) : isMobile ? (
        /* 手機版卡片列表 */
        <div>
          {transactions.length === 0 ? (
            <div className="text-center text-muted py-4">
              目前沒有交易記錄
            </div>
          ) : (
            transactions.map(txn => (
              <Card
                key={txn.txn_id}
                className="mb-2 shadow-sm"
                style={{ borderLeft: `4px solid ${txn.direction === 'income' ? '#28a745' : '#dc3545'}` }}
                onClick={() => viewDetail(txn.txn_id)}
              >
                <Card.Body className="p-2">
                  {/* 頂部：日期 + 類型標籤 */}
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-muted">
                      {new Date(txn.txn_date).toLocaleDateString('zh-TW')}
                    </small>
                    <div>
                      <Badge bg={getDirectionColor(txn.direction)} className="me-1">
                        {getDirectionText(txn.direction)}
                      </Badge>
                      <Badge bg="secondary">{getSourceText(txn.source)}</Badge>
                    </div>
                  </div>

                  {/* 科目 + 金額 */}
                  <div className="d-flex justify-content-between align-items-center">
                    <div style={{ fontSize: '13px' }}>
                      <strong>{txn.s_code}</strong>
                      <span className="text-muted ms-1">{txn.s_title}</span>
                    </div>
                    <div className={`fw-bold ${txn.direction === 'income' ? 'text-success' : 'text-danger'}`}>
                      {txn.direction === 'income' ? '+' : '-'}{formatAmount(txn.amount)}
                    </div>
                  </div>

                  {/* 備註（如果有） */}
                  {txn.memo && (
                    <small className="text-muted d-block mt-1 text-truncate">
                      {txn.memo}
                    </small>
                  )}
                </Card.Body>
              </Card>
            ))
          )}
        </div>
      ) : (
        /* 桌面版表格 */
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th style={{ width: '80px' }}>ID</th>
              <th style={{ width: '100px' }}>日期</th>
              <th style={{ width: '80px' }}>類型</th>
              <th>科目</th>
              <th style={{ width: '120px' }}>金額</th>
              <th style={{ width: '100px' }}>付款方式</th>
              <th style={{ width: '100px' }}>來源</th>
              <th>備註</th>
              <th style={{ width: '100px' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center text-muted py-4">
                  目前沒有交易記錄
                </td>
              </tr>
            ) : (
              transactions.map(txn => (
                <tr key={txn.txn_id}>
                  <td><code>{txn.txn_id}</code></td>
                  <td>{new Date(txn.txn_date).toLocaleDateString('zh-TW')}</td>
                  <td>
                    <Badge bg={getDirectionColor(txn.direction)}>
                      {getDirectionText(txn.direction)}
                    </Badge>
                  </td>
                  <td>{txn.s_code} - {txn.s_title}</td>
                  <td className="text-end">
                    <span className={txn.direction === 'income' ? 'text-success' : 'text-danger'}>
                      {txn.direction === 'income' ? '+' : '-'}{formatAmount(txn.amount)}
                    </span>
                  </td>
                  <td>{getPaymentMethodText(txn.payment_method)}</td>
                  <td>
                    <Badge bg="secondary">{getSourceText(txn.source)}</Badge>
                  </td>
                  <td className="text-truncate" style={{ maxWidth: '200px' }}>
                    {txn.memo || '-'}
                  </td>
                  <td>
                    <ButtonGroup size="sm">
                      <Button
                        variant="outline-primary"
                        onClick={() => viewDetail(txn.txn_id)}
                      >
                        查看
                      </Button>
                    </ButtonGroup>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* 新增交易 Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>手動入帳</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>收支類型 <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={newTransaction.direction}
                    onChange={(e) => setNewTransaction({ ...newTransaction, direction: e.target.value })}
                  >
                    <option value="income">收入</option>
                    <option value="expense">支出</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>科目 <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={newTransaction.s_id}
                    onChange={(e) => setNewTransaction({ ...newTransaction, s_id: e.target.value })}
                  >
                    <option value="">請選擇科目</option>
                    {subjects
                      .filter(s => s.s_type === newTransaction.direction)
                      .map(s => (
                        <option key={s.s_id} value={s.s_id}>
                          {s.s_code} - {s.s_title}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>金額 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={newTransaction.amount}
                    onChange={(e) => setNewTransaction({ ...newTransaction, amount: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>交易日期 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={newTransaction.txn_date}
                    onChange={(e) => setNewTransaction({ ...newTransaction, txn_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>付款方式</Form.Label>
                  <Form.Select
                    value={newTransaction.payment_method}
                    onChange={(e) => setNewTransaction({ ...newTransaction, payment_method: e.target.value })}
                  >
                    <option value="transfer">轉帳</option>
                    <option value="cash">現金</option>
                    <option value="check">支票</option>
                    <option value="other">其他</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>憑證編號</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTransaction.payment_ref}
                    onChange={(e) => setNewTransaction({ ...newTransaction, payment_ref: e.target.value })}
                    placeholder="請輸入憑證編號（選填）"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>備註</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newTransaction.memo}
                onChange={(e) => setNewTransaction({ ...newTransaction, memo: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            新增
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 交易詳情 Modal */}
      {currentDetail && (
        <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>交易記錄詳情 #{currentDetail.txn_id}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <strong>交易日期:</strong> {new Date(currentDetail.txn_date).toLocaleDateString('zh-TW')}<br />
                <strong>類型:</strong> <Badge bg={getDirectionColor(currentDetail.direction)}>{getDirectionText(currentDetail.direction)}</Badge><br />
                <strong>科目:</strong> {currentDetail.s_code} - {currentDetail.s_title}<br />
                <strong>金額:</strong> <span className={currentDetail.direction === 'income' ? 'text-success' : 'text-danger'}>
                  {formatAmount(currentDetail.amount)}
                </span>
              </Col>
              <Col md={6}>
                <strong>來源:</strong> {getSourceText(currentDetail.source)}<br />
                <strong>付款方式:</strong> {getPaymentMethodText(currentDetail.payment_method)}<br />
                <strong>憑證編號:</strong> {currentDetail.payment_ref || '-'}<br />
                <strong>建立者:</strong> {currentDetail.creator_name || '-'}
              </Col>
            </Row>

            {currentDetail.ref_req_id && (
              <Alert variant="info">
                <strong>關聯請款單:</strong> #{currentDetail.ref_req_id}
                {currentDetail.ref_item_id && ` (明細 #${currentDetail.ref_item_id})`}
              </Alert>
            )}

            {currentDetail.memo && (
              <Alert variant="secondary">
                <strong>備註:</strong> {currentDetail.memo}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            {currentDetail.source === 'manual' && isAdmin && (
              <Button
                variant="outline-danger"
                onClick={() => deleteTransaction(currentDetail.txn_id, currentDetail.source)}
              >
                🗑️ 刪除
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
              關閉
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
}

export default TransactionManagement;
